import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Alert, Badge, Button, Form, Modal, Row, Col, Spinner, Table } from 'react-bootstrap';
import { 
  Network, 
  Table as TableIcon, 
  ZoomIn, 
  ZoomOut, 
  RotateCcw, 
  Plus, 
  Edit2, 
  Trash2, 
  Search, 
  Maximize2, 
  Minimize2, 
  Building2, 
  UserTie,
  User,
  Shield,
  Layers,
  GripVertical
} from 'lucide-react';
import { message, Tooltip } from 'antd';

import { getManagers } from '../../services/managerService';
import { 
  createOrganizationNode, 
  deleteOrganizationNode, 
  getOrganizationNodes, 
  updateOrganizationNode 
} from '../../services/organizationService';
import './OrganizationManagement.scss';

const emptyForm = {
  guid: null,
  parentGuid: '',
  managerGuid: '',
  name: '',
  title: '',
  nodeType: 'department',
  imageUrl: '',
  description: '',
  sequenceNr: 0,
  isActive: true
};

const typeLabels = {
  mayor: 'Belediye Başkanı',
  viceMayor: 'Başkan Yardımcısı',
  group: 'Makama Bağlı / Grup',
  department: 'Müdürlük',
  subDepartment: 'Alt Birim / Şeflik'
};

const OrganizationManagement = () => {
  const [nodes, setNodes] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // UI states
  const [viewMode, setViewMode] = useState('chart'); // 'chart' | 'table'
  const [searchQuery, setSearchQuery] = useState('');
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);

  // Drag and drop state
  const [draggedGuid, setDraggedGuid] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // { colGuid, deptGuid, position: 'before' | 'after' | 'inside' }

  const canvasRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [organizationData, managerData] = await Promise.all([
        getOrganizationNodes(),
        getManagers()
      ]);
      setNodes(Array.isArray(organizationData) ? organizationData : []);
      setManagers(Array.isArray(managerData) ? managerData : managerData?.data || []);
    } catch (err) {
      setError(err?.response?.data?.resultMessage || err.message || 'Organizasyon şeması yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Manager lookup helper
  const getManagerForNode = useCallback((node) => {
    if (!node) return null;
    if (node.manager) return node.manager;
    if (node.managerGuid) {
      return managers.find(m => m.guid === node.managerGuid) || null;
    }
    return null;
  }, [managers]);

  // Image resolver
  const getNodeImage = useCallback((node) => {
    if (node.imageUrl) return node.imageUrl;
    const mgr = getManagerForNode(node);
    if (mgr?.pictureBase64) return mgr.pictureBase64;
    if (mgr?.pictureUrl) return mgr.pictureUrl;
    return null;
  }, [getManagerForNode]);

  // Initials generator
  const getInitials = (name) => {
    if (!name) return 'ÖB';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Build hierarchical tree structure
  const { rootNodes, treeMap } = useMemo(() => {
    const map = new Map();
    nodes.forEach(n => {
      map.set(n.guid, { ...n, children: [] });
    });

    const roots = [];
    nodes.forEach(n => {
      const item = map.get(n.guid);
      if (n.parentGuid && map.has(n.parentGuid)) {
        map.get(n.parentGuid).children.push(item);
      } else {
        roots.push(item);
      }
    });

    const sortSubtree = (list) => {
      list.sort((a, b) => (Number(a.sequenceNr) || 0) - (Number(b.sequenceNr) || 0) || a.name.localeCompare(b.name, 'tr'));
      list.forEach(item => sortSubtree(item.children));
    };

    sortSubtree(roots);
    return { rootNodes: roots, treeMap: map };
  }, [nodes]);

  // Flat ordered list for table view & parent selector
  const orderedNodes = useMemo(() => {
    const result = [];
    const visit = (nodeList, depth = 0, visited = new Set()) => {
      nodeList.forEach(node => {
        if (visited.has(node.guid)) return;
        visited.add(node.guid);
        result.push({ ...node, depth });
        if (node.children?.length) {
          visit(node.children, depth + 1, visited);
        }
      });
    };
    visit(rootNodes, 0);
    return result;
  }, [rootNodes]);

  // Search filtering
  const searchFilter = searchQuery.trim().toLowerCase();
  const hasSearch = searchFilter.length > 0;

  const matchesSearch = useCallback((node) => {
    if (!hasSearch) return false;
    const nameMatch = node.name?.toLowerCase().includes(searchFilter);
    const titleMatch = node.title?.toLowerCase().includes(searchFilter);
    const manager = getManagerForNode(node);
    const managerMatch = manager?.name?.toLowerCase().includes(searchFilter);
    return nameMatch || titleMatch || managerMatch;
  }, [hasSearch, searchFilter, getManagerForNode]);

  // Form actions
  const openCreate = (parentGuid = '', defaultType = 'department') => {
    const parentNode = nodes.find(n => n.guid === parentGuid);
    let inferredType = defaultType;
    if (parentNode) {
      if (parentNode.nodeType === 'mayor') inferredType = 'viceMayor';
      else if (parentNode.nodeType === 'viceMayor' || parentNode.nodeType === 'group') inferredType = 'department';
      else inferredType = 'subDepartment';
    }

    const siblingCount = nodes.filter(n => (n.parentGuid || '') === (parentGuid || '')).length;

    setForm({
      ...emptyForm,
      parentGuid: parentGuid || '',
      nodeType: inferredType,
      sequenceNr: siblingCount + 1
    });
    setShowModal(true);
  };

  const openEdit = (node) => {
    setForm({
      ...emptyForm,
      ...node,
      parentGuid: node.parentGuid || '',
      managerGuid: node.managerGuid || '',
      imageUrl: node.imageUrl || '',
      description: node.description || ''
    });
    setShowModal(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        parentGuid: form.parentGuid || null,
        managerGuid: form.managerGuid || null,
        sequenceNr: Number(form.sequenceNr) || 0
      };
      const response = form.guid 
        ? await updateOrganizationNode(payload) 
        : await createOrganizationNode(payload);

      if (response?.isSuccess === false) throw new Error(response.resultMessage);
      message.success(form.guid ? 'Organizasyon kaydı güncellendi.' : 'Organizasyon kaydı eklendi.');
      setShowModal(false);
      await load();
    } catch (err) {
      message.error(err?.response?.data?.resultMessage || err.message || 'Kayıt işlemi başarısız.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (node) => {
    if (!window.confirm(`“${node.name}” birimini pasif yapmak / silmek istediğinize emin misiniz?`)) return;
    try {
      const response = await deleteOrganizationNode(node.guid);
      if (response?.isSuccess === false) throw new Error(response.resultMessage);
      message.success('Organizasyon kaydı pasif yapıldı.');
      await load();
    } catch (err) {
      message.error(err?.response?.data?.resultMessage || err.message || 'Kayıt silinemedi.');
    }
  };

  // Zoom handlers
  const handleZoomIn = () => setZoom(z => Math.min(1.6, +(z + 0.1).toFixed(1)));
  const handleZoomOut = () => setZoom(z => Math.max(0.4, +(z - 0.1).toFixed(1)));
  const handleZoomReset = () => setZoom(1);

  // Drag & drop handlers
  const handleDragStart = (e, dept) => {
    e.dataTransfer.setData('text/plain', dept.guid);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedGuid(dept.guid);
  };

  const handleDragOverDept = (e, dept, colGuid) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedGuid || draggedGuid === dept.guid) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const position = e.clientY < midY ? 'before' : 'after';
    setDropTarget({ colGuid, deptGuid: dept.guid, position });
  };

  const handleDragOverColumn = (e, colGuid) => {
    e.preventDefault();
    if (!draggedGuid) return;
    if (!dropTarget || dropTarget.colGuid !== colGuid || dropTarget.deptGuid !== null) {
      setDropTarget({ colGuid, deptGuid: null, position: 'inside' });
    }
  };

  const handleDragEnd = () => {
    setDraggedGuid(null);
    setDropTarget(null);
  };

  const handleDrop = async (e, targetColGuid, targetDeptGuid = null, position = 'inside') => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedGuid) return;
    const draggedItem = nodes.find(n => n.guid === draggedGuid);
    if (!draggedItem) {
      handleDragEnd();
      return;
    }
    if (draggedItem.guid === targetDeptGuid) {
      handleDragEnd();
      return;
    }

    // Siblings under targetColGuid except the dragged item
    const existingSiblings = nodes
      .filter(n => n.parentGuid === targetColGuid && n.guid !== draggedItem.guid)
      .sort((a, b) => (Number(a.sequenceNr) || 0) - (Number(b.sequenceNr) || 0) || a.name.localeCompare(b.name, 'tr'));

    let insertIndex = existingSiblings.length;
    if (targetDeptGuid) {
      const idx = existingSiblings.findIndex(s => s.guid === targetDeptGuid);
      if (idx !== -1) {
        insertIndex = position === 'before' ? idx : idx + 1;
      }
    }

    const reordered = [...existingSiblings];
    reordered.splice(insertIndex, 0, draggedItem);

    // Compute updated records
    const updatedSiblings = reordered.map((item, idx) => ({
      ...item,
      parentGuid: targetColGuid,
      sequenceNr: idx + 1
    }));

    // Optimistic update
    setNodes(prev => {
      const updateMap = new Map(updatedSiblings.map(u => [u.guid, u]));
      return prev.map(n => updateMap.get(n.guid) || n);
    });

    handleDragEnd();

    // Persist to backend
    const mainUpdate = updatedSiblings.find(u => u.guid === draggedItem.guid);
    try {
      await updateOrganizationNode({
        ...mainUpdate,
        parentGuid: mainUpdate.parentGuid || null,
        managerGuid: mainUpdate.managerGuid || null,
        sequenceNr: Number(mainUpdate.sequenceNr) || 0
      });

      // Update remaining siblings sequence numbers in background
      const otherUpdates = updatedSiblings.filter(u => u.guid !== draggedItem.guid);
      await Promise.allSettled(otherUpdates.map(u => updateOrganizationNode({
        ...u,
        parentGuid: u.parentGuid || null,
        managerGuid: u.managerGuid || null,
        sequenceNr: Number(u.sequenceNr) || 0
      })));

      const targetCol = nodes.find(n => n.guid === targetColGuid);
      message.success(`“${draggedItem.name}” ${targetCol ? targetCol.name : 'yeni konumuna'} taşındı.`);
    } catch (err) {
      message.error('Taşıma sırasında hata oluştu.');
      await load();
    }
  };

  // Render a department pill (Level 2+)
  const renderDepartmentPill = (dept, colGuid) => {
    const isHit = matchesSearch(dept);
    const isDim = hasSearch && !isHit;
    const mgr = getManagerForNode(dept);
    const isDragging = draggedGuid === dept.guid;
    const isDropBefore = dropTarget?.deptGuid === dept.guid && dropTarget?.position === 'before';
    const isDropAfter = dropTarget?.deptGuid === dept.guid && dropTarget?.position === 'after';

    return (
      <div 
        key={dept.guid} 
        className="dept-item-wrapper"
        onDragOver={(e) => handleDragOverDept(e, dept, colGuid)}
        onDrop={(e) => handleDrop(e, colGuid, dept.guid, dropTarget?.position || 'before')}
      >
        {isDropBefore && <div className="drop-indicator-line" />}

        <Tooltip title={mgr ? `Müdür: ${mgr.name} (Sürükleyip bırakabilirsiniz)` : `${dept.description || dept.name} (Sürükleyip bırakabilirsiniz)`} placement="top">
          <div 
            className={`org-dept-pill ${isHit ? 'is-highlighted' : ''} ${isDim ? 'is-dimmed' : ''} ${isDragging ? 'is-dragging' : ''}`}
            draggable={true}
            onDragStart={(e) => handleDragStart(e, dept)}
            onDragEnd={handleDragEnd}
            onClick={() => openEdit(dept)}
          >
            <GripVertical size={13} className="drag-handle" />

            <div className="org-card-actions">
              <button 
                className="action-edit" 
                title="Düzenle" 
                onClick={(e) => { e.stopPropagation(); openEdit(dept); }}
              >
                <Edit2 size={12} />
              </button>
              <button 
                className="action-add" 
                title="Alt Birim Ekle" 
                onClick={(e) => { e.stopPropagation(); openCreate(dept.guid, 'subDepartment'); }}
              >
                <Plus size={12} />
              </button>
              <button 
                className="action-del" 
                title="Sil" 
                onClick={(e) => { e.stopPropagation(); remove(dept); }}
              >
                <Trash2 size={12} />
              </button>
            </div>

            <span className="pill-text">{dept.name}</span>
            {mgr && <span className="manager-dot" title={`Yönetici: ${mgr.name}`} />}
          </div>
        </Tooltip>

        {isDropAfter && <div className="drop-indicator-line" />}

        {/* Sub-departments if any */}
        {dept.children && dept.children.length > 0 && (
          <div style={{ paddingLeft: '16px', width: '100%', marginTop: '6px' }}>
            {dept.children.map(subDept => renderDepartmentPill(subDept, colGuid))}
          </div>
        )}
      </div>
    );
  };

  // Render a Level 1 column (Vice Mayor or Group)
  const renderColumn = (colNode) => {
    const image = getNodeImage(colNode);
    const isHit = matchesSearch(colNode);
    const isDim = hasSearch && !isHit;
    const isGroup = colNode.nodeType === 'group' || (!image && !colNode.managerGuid);

    return (
      <div key={colNode.guid} className="org-column">
        {/* Top connector stem from horizontal bus */}
        <div className="column-top-stem" />

        {/* Column Header Card */}
        <div 
          className={`org-card-vicemayor ${isGroup ? 'is-group-card' : ''} ${isHit ? 'is-highlighted' : ''} ${isDim ? 'is-dimmed' : ''}`}
          onClick={() => openEdit(colNode)}
        >
          <div className="org-card-actions">
            <button 
              className="action-edit" 
              title="Düzenle" 
              onClick={(e) => { e.stopPropagation(); openEdit(colNode); }}
            >
              <Edit2 size={12} />
            </button>
            <button 
              className="action-add" 
              title="Müdürlük Ekle" 
              onClick={(e) => { e.stopPropagation(); openCreate(colNode.guid, 'department'); }}
            >
              <Plus size={12} />
            </button>
            <button 
              className="action-del" 
              title="Sil" 
              onClick={(e) => { e.stopPropagation(); remove(colNode); }}
            >
              <Trash2 size={12} />
            </button>
          </div>

          {!isGroup && (
            <div className="avatar-wrapper">
              {image ? (
                <img src={image} alt={colNode.name} />
              ) : (
                <div className="avatar-fallback">
                  {getInitials(colNode.name)}
                </div>
              )}
            </div>
          )}

          <div className="card-name">{colNode.name}</div>
          {colNode.title && <div className="card-title">{colNode.title}</div>}
        </div>

        {/* Stem connecting header to departments list */}
        <div className="column-mid-stem" />

        {/* Departments Pills Column */}
        <div 
          className={`org-dept-list ${dropTarget?.colGuid === colNode.guid && dropTarget?.deptGuid === null ? 'is-drag-over-col' : ''}`}
          onDragOver={(e) => handleDragOverColumn(e, colNode.guid)}
          onDrop={(e) => handleDrop(e, colNode.guid, null, 'inside')}
        >
          {(colNode.children || []).map(dept => renderDepartmentPill(dept, colNode.guid))}

          {/* Quick Add Button at bottom of column */}
          <button 
            className="btn-add-dept-col" 
            onClick={() => openCreate(colNode.guid, 'department')}
            title="Bu birimin altına yeni müdürlük ekle"
          >
            <Plus size={13} strokeWidth={2.4} />
            <span>Müdürlük Ekle</span>
          </button>
        </div>
      </div>
    );
  };

  // Render Root Node (Mayor / Başkan) and its branches
  const renderRootBranch = (root) => {
    const image = getNodeImage(root);
    const isHit = matchesSearch(root);
    const isDim = hasSearch && !isHit;
    const columns = root.children || [];
    const hasColumns = columns.length > 0;

    return (
      <div key={root.guid} className="org-tree">
        {/* Level 0: Mayor Card */}
        <div 
          className={`org-card-mayor ${isHit ? 'is-highlighted' : ''} ${isDim ? 'is-dimmed' : ''}`}
          onClick={() => openEdit(root)}
        >
          <div className="org-card-actions">
            <button 
              className="action-edit" 
              title="Düzenle" 
              onClick={(e) => { e.stopPropagation(); openEdit(root); }}
            >
              <Edit2 size={13} />
            </button>
            <button 
              className="action-add" 
              title="Bağlı Birim / Bşk. Yrd. Ekle" 
              onClick={(e) => { e.stopPropagation(); openCreate(root.guid, 'viceMayor'); }}
            >
              <Plus size={13} />
            </button>
            <button 
              className="action-del" 
              title="Sil" 
              onClick={(e) => { e.stopPropagation(); remove(root); }}
            >
              <Trash2 size={13} />
            </button>
          </div>

          <div className="avatar-wrapper">
            {image ? (
              <img src={image} alt={root.name} />
            ) : (
              <div className="avatar-fallback">
                {getInitials(root.name)}
              </div>
            )}
          </div>

          <h3 className="card-name">{root.name}</h3>
          <p className="card-title">{root.title || 'İskenderun Belediye Başkanı'}</p>

          <span className={`status-pill ${root.isActive ? 'active' : 'inactive'}`}>
            {root.isActive ? 'Aktif' : 'Pasif'}
          </span>
        </div>

        {hasColumns && (
          <>
            {/* Stem line directly under Mayor card */}
            <div className="stem-vertical" style={{ height: '32px' }} />

            {/* Horizontal Bus Line connecting all Level 1 columns */}
            <div className="bus-horizontal-container">
              {columns.length > 1 ? (
                <div 
                  className="bus-line" 
                  style={{
                    /* Span from center of first column to center of last column */
                    left: `${100 / (columns.length * 2)}%`,
                    right: `${100 / (columns.length * 2)}%`
                  }} 
                />
              ) : (
                <div className="bus-drop-line" style={{ left: '50%', transform: 'translateX(-50%)' }} />
              )}
            </div>

            {/* Level 1 Columns Row */}
            <div className="org-columns-row">
              {columns.map(col => renderColumn(col))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="organization-management">
      {/* Header Toolbar */}
      <div className="org-header-card">
        <div className="org-header-title">
          <h2>
            <Network size={24} />
            <span>Organizasyon Şeması</span>
          </h2>
          <p>Hiyerarşik belediye yönetim yapısı, başkanlık, başkan yardımcıları ve bağlı müdürlükler.</p>
        </div>

        <div className="org-header-tools">
          {/* Quick Search */}
          <div className="org-search-box">
            <Search size={14} />
            <input 
              type="text" 
              placeholder="Birim veya kişi ara..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* View Mode Toggle */}
          <div className="org-view-toggle">
            <button 
              className={viewMode === 'chart' ? 'active' : ''} 
              onClick={() => setViewMode('chart')}
              title="Görsel Hiyerarşik Şema"
            >
              <Network size={14} />
              <span>Görsel Şema</span>
            </button>
            <button 
              className={viewMode === 'table' ? 'active' : ''} 
              onClick={() => setViewMode('table')}
              title="Liste / Tablo Görünümü"
            >
              <TableIcon size={14} />
              <span>Tablo</span>
            </button>
          </div>

          {/* Drag & Drop Hint */}
          {viewMode === 'chart' && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              background: 'var(--arc-surface-subtle)',
              border: '1px solid var(--arc-border)',
              borderRadius: 'var(--arc-radius-md)',
              fontSize: '0.78rem',
              color: 'var(--arc-text-secondary)',
              fontWeight: 500
            }}>
              <GripVertical size={13} style={{ color: 'var(--arc-primary)' }} />
              <span>Sürükleyip bırakarak taşıyabilirsiniz</span>
            </div>
          )}

          {/* Zoom Controls (Active in Chart mode) */}
          {viewMode === 'chart' && (
            <div className="org-zoom-controls">
              <button onClick={handleZoomOut} title="Uzaklaştır"><ZoomOut size={14} /></button>
              <span className="zoom-level">{Math.round(zoom * 100)}%</span>
              <button onClick={handleZoomIn} title="Yakınlaştır"><ZoomIn size={14} /></button>
              <button onClick={handleZoomReset} title="Sıfırla (100%)"><RotateCcw size={13} /></button>
              <button 
                onClick={() => setIsFullscreen(!isFullscreen)} 
                title={isFullscreen ? 'Tam Ekrandan Çık' : 'Tam Ekran'}
              >
                {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
              </button>
            </div>
          )}

          {/* Add Primary Action */}
          <button className="btn-add-node" onClick={() => openCreate('', 'mayor')}>
            <Plus size={16} strokeWidth={2.4} />
            <span>Yeni Kayıt</span>
          </button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Main Content Area */}
      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Organizasyon şeması yükleniyor...</p>
        </div>
      ) : viewMode === 'chart' ? (
        /* VISUAL ORG CHART VIEW */
        <div className={`org-chart-wrapper ${isFullscreen ? 'is-fullscreen' : ''}`}>
          <div className="org-chart-canvas" ref={canvasRef}>
            <div 
              className="org-chart-viewport" 
              style={{ transform: `scale(${zoom})` }}
            >
              {rootNodes.length > 0 ? (
                rootNodes.map(root => renderRootBranch(root))
              ) : (
                <div className="text-center py-5 text-muted">
                  <Network size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
                  <p>Henüz tanımlanmış bir organizasyon birimi bulunmuyor.</p>
                  <Button variant="primary" onClick={() => openCreate('', 'mayor')}>
                    İlk Birimi Ekle (Belediye Başkanı)
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* TABULAR VIEW */
        <div className="org-table-card">
          <div className="table-responsive">
            <Table hover className="align-middle">
              <thead>
                <tr>
                  <th>Organizasyon Kaydı</th>
                  <th>Tür</th>
                  <th>Bağlı Müdür / Yönetici</th>
                  <th>Sıra</th>
                  <th>Durum</th>
                  <th className="text-end">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {orderedNodes.map((node) => {
                  const image = getNodeImage(node);
                  const mgr = getManagerForNode(node);
                  const isHit = matchesSearch(node);

                  return (
                    <tr key={node.guid} style={{ background: isHit ? 'var(--arc-primary-subtle)' : undefined }}>
                      <td>
                        <div className="org-table-node" style={{ paddingLeft: `${node.depth * 28}px` }}>
                          {image ? (
                            <img src={image} alt="" className="node-thumb" />
                          ) : (
                            <div className="node-thumb-fallback">
                              {getInitials(node.name)}
                            </div>
                          )}
                          <div className="node-name-info">
                            <strong>{node.name}</strong>
                            {node.title && <small>{node.title}</small>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge bg={
                          node.nodeType === 'mayor' ? 'primary' :
                          node.nodeType === 'viceMayor' ? 'warning' :
                          node.nodeType === 'group' ? 'info' : 'success'
                        }>
                          {typeLabels[node.nodeType] || node.nodeType}
                        </Badge>
                      </td>
                      <td>
                        {mgr ? (
                          <div>
                            <span style={{ fontWeight: 600 }}>{mgr.name}</span>
                            {mgr.positionName && <div style={{ fontSize: '0.75rem', color: 'var(--arc-text-muted)' }}>{mgr.positionName}</div>}
                          </div>
                        ) : (
                          <span className="text-muted">Atanmamış</span>
                        )}
                      </td>
                      <td>{node.sequenceNr}</td>
                      <td>
                        <Badge bg={node.isActive ? 'success' : 'secondary'}>
                          {node.isActive ? 'Aktif' : 'Pasif'}
                        </Badge>
                      </td>
                      <td className="text-end text-nowrap">
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          className="me-2" 
                          onClick={() => openEdit(node)}
                          title="Düzenle"
                        >
                          <Edit2 size={13} />
                        </Button>
                        <Button 
                          variant="outline-success" 
                          size="sm" 
                          className="me-2" 
                          onClick={() => openCreate(node.guid)}
                          title="Altına Birim Ekle"
                        >
                          <Plus size={13} />
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          onClick={() => remove(node)}
                          title="Sil / Pasif Yap"
                        >
                          <Trash2 size={13} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {!orderedNodes.length && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-5">
                      Henüz organizasyon kaydı yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {/* CREATE / EDIT MODAL */}
      <Modal 
        show={showModal} 
        onHide={() => !saving && setShowModal(false)} 
        size="lg" 
        centered
      >
        <Form onSubmit={submit}>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--arc-text-primary)' }}>
              {form.guid ? 'Organizasyon Birimini Düzenle' : 'Yeni Organizasyon Birimi'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem' }}>Birim Türü *</Form.Label>
                  <Form.Select 
                    value={form.nodeType} 
                    onChange={(e) => setForm({ ...form, nodeType: e.target.value })} 
                    required
                  >
                    {Object.entries(typeLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem' }}>Üst Birim</Form.Label>
                  <Form.Select 
                    value={form.parentGuid} 
                    onChange={(e) => setForm({ ...form, parentGuid: e.target.value })}
                  >
                    <option value="">Ana Seviye (En Üst Kök)</option>
                    {orderedNodes
                      .filter((node) => node.guid !== form.guid)
                      .map((node) => (
                        <option key={node.guid} value={node.guid}>
                          {'— '.repeat(node.depth)}{node.name} ({typeLabels[node.nodeType] || node.nodeType})
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={7}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem' }}>Birim / Kişi Adı *</Form.Label>
                  <Form.Control 
                    value={form.name} 
                    onChange={(e) => setForm({ ...form, name: e.target.value })} 
                    maxLength={200} 
                    placeholder="Örn: Fen İşleri Md. veya MEHMET DÖNMEZ"
                    required 
                  />
                </Form.Group>
              </Col>

              <Col md={5}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem' }}>Ünvan / Pozisyon</Form.Label>
                  <Form.Control 
                    value={form.title} 
                    onChange={(e) => setForm({ ...form, title: e.target.value })} 
                    maxLength={200} 
                    placeholder="Örn: Belediye Bşk. Yrd."
                  />
                </Form.Group>
              </Col>

              <Col md={8}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem' }}>Bağlı Müdür / Yönetici</Form.Label>
                  <Form.Select 
                    value={form.managerGuid} 
                    onChange={(e) => {
                      const mgrId = e.target.value;
                      const selMgr = managers.find(m => m.guid === mgrId);
                      setForm(prev => ({
                        ...prev,
                        managerGuid: mgrId,
                        // If title is empty and manager selected, auto-fill position
                        title: prev.title || selMgr?.positionName || prev.title
                      }));
                    }}
                  >
                    <option value="">Müdür atanmamış</option>
                    {managers.map((manager) => (
                      <option key={manager.guid} value={manager.guid}>
                        {manager.name} {manager.positionName ? `— ${manager.positionName}` : ''}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Yöneticinin fotoğraf ve iletişim detayları Müdürlerimiz modülünden otomatik çekilir.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem' }}>Görüntüleme Sırası</Form.Label>
                  <Form.Control 
                    type="number" 
                    min="0" 
                    value={form.sequenceNr} 
                    onChange={(e) => setForm({ ...form, sequenceNr: e.target.value })} 
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem' }}>Özel Görsel URL (İsteğe Bağlı)</Form.Label>
                  <Form.Control 
                    value={form.imageUrl} 
                    onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} 
                    maxLength={1000} 
                    placeholder="Yönetici fotoğrafı harici özel görsel URL'si"
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label style={{ fontWeight: 600, fontSize: '0.82rem' }}>Açıklama / Görev Alanı</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={2} 
                    value={form.description} 
                    onChange={(e) => setForm({ ...form, description: e.target.value })} 
                    maxLength={2000} 
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Check 
                  type="switch" 
                  id="active-switch" 
                  label="Aktif (Şemada ve sitede görüntülensin)" 
                  checked={form.isActive} 
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })} 
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowModal(false)} 
              disabled={saving}
            >
              Vazgeç
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={saving}
              style={{ background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)', border: 'none' }}
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default OrganizationManagement;
